import type { EnhancedDeal, RfqPackage, RfqProject, RfqSubmission } from '../types';
import { parseLegacyTitle } from './projectName';
import { submissionQuoteTotal } from './rfqQuoteTotal';
import { cncSubmissionHistoryKey, groupQuoteSubmissionHistory } from './rfqSubmissionHistory';
import { timberQuoteStreamKey } from './timberQuoteStreamKey';
import { primarySubmissionsFromGroups } from './vendorPortalQuoteDisplay';
import { approvedSubmissionIdsForPackage } from './vendorPortalApprovals';
import { normalizeRfqPackage, normalizeRfqProject, normalizeWorkType } from './vendorHelpers';
import { buildVendorPackageVersions, projectNeedsQuoteResponse } from './vendorPackageVersion';
import { activeScopeStructures, packageScopeFullyArchived } from './rfqPackageArchivedStructures';

export type VendorPortalWorkGroup = {
  project: RfqProject;
  packages: RfqPackage[];
};

export type VendorPortalDealGroup = {
  dealId: string;
  title: string;
  timber: VendorPortalWorkGroup | null;
  cnc: VendorPortalWorkGroup | null;
};

function sortPackages(packages: RfqPackage[]): RfqPackage[] {
  return packages.slice().sort((a, b) => {
    const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
    const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
    return tb - ta;
  });
}

function resolveProject(
  projectId: string,
  orgId: string,
  dealId: string,
  title: string,
  workType: RfqProject['workType'],
  pkg: RfqPackage,
  projectById: Map<string, RfqProject>,
): RfqProject {
  const existing = projectById.get(projectId);
  if (existing) return existing;
  return {
    id: projectId,
    supplierOrgId: orgId,
    dealId,
    dealTitle: title,
    status: 'active',
    workType,
    createdAt: pkg.createdAt instanceof Date ? pkg.createdAt : new Date(),
  };
}

/** Group supplier packages by deal — one sidebar row per project, timber + CNC combined. */
export function buildVendorPortalDealGroups(
  orgId: string | undefined,
  rfqProjects: RfqProject[],
  rfqPackages: RfqPackage[],
): VendorPortalDealGroup[] {
  if (!orgId) return [];

  const projectById = new Map(
    rfqProjects.filter(p => p.id).map(p => [p.id as string, normalizeRfqProject(p)]),
  );
  const pkgsForOrg = rfqPackages
    .filter(p => p.supplierOrgId === orgId)
    .map(normalizeRfqPackage);

  const byDeal = new Map<string, VendorPortalDealGroup>();

  for (const pkg of pkgsForOrg) {
    const dealId = pkg.dealId?.trim();
    if (!dealId || !pkg.rfqProjectId) continue;

    const title = (pkg.dealTitle || dealId).trim();
    let group = byDeal.get(dealId);
    if (!group) {
      group = { dealId, title, timber: null, cnc: null };
      byDeal.set(dealId, group);
    }
    if (pkg.dealTitle?.trim()) group.title = pkg.dealTitle.trim();

    const workType = normalizeWorkType(pkg.workType);
    const project = resolveProject(
      pkg.rfqProjectId,
      orgId,
      dealId,
      group.title,
      workType,
      pkg,
      projectById,
    );
    const bucket = workType === 'cnc_quote' ? 'cnc' : 'timber';
    const existing = group[bucket];
    if (!existing) {
      group[bucket] = { project, packages: [pkg] };
    } else {
      existing.packages.push(pkg);
    }
  }

  Array.from(byDeal.values()).forEach(group => {
    if (group.timber) group.timber.packages = sortPackages(group.timber.packages);
    if (group.cnc) group.cnc.packages = sortPackages(group.cnc.packages);
  });

  return Array.from(byDeal.values()).sort((a, b) => {
    const newest = (g: VendorPortalDealGroup) => {
      const times: number[] = [];
      for (const pkg of g.timber?.packages ?? []) {
        times.push(pkg.createdAt instanceof Date ? pkg.createdAt.getTime() : 0);
      }
      for (const pkg of g.cnc?.packages ?? []) {
        times.push(pkg.createdAt instanceof Date ? pkg.createdAt.getTime() : 0);
      }
      return times.length ? Math.max(...times) : 0;
    };
    return newest(b) - newest(a);
  });
}

export const VENDOR_WHOLE_PROJECT_KEY = '__whole_project__';

export type VendorStructurePackageGroup = {
  /** Stable key for the structure set (`__whole_project__` when untagged). */
  key: string;
  /** "Barn", "Barn + Porch", or "Whole project". */
  label: string;
  structureIds: string[];
  structureNames: string[];
  /** Newest first — same order the version tree expects. */
  packages: RfqPackage[];
};

function packageStructureRefs(pkg: RfqPackage): { id: string; name: string }[] {
  if (packageScopeFullyArchived(pkg)) return [];
  const scoped = activeScopeStructures(pkg.scopeStructures, pkg.archivedStructureIds);
  if (scoped.length) return scoped.map(s => ({ id: s.id, name: s.name || '' }));
  const cnc = (pkg.cncPackagePayload?.structures ?? []).filter(s => s?.id);
  return cnc.map(s => ({ id: s.id, name: s.name || '' }));
}

/**
 * Split one supplier's packages (single work type) into lanes per structure
 * set, mirroring how the Project Hub forks a whole-project track into
 * per-structure tracks. Whole-project packages sent before the split are the
 * shared history, so they appear in every structure lane rather than in a lane
 * of their own. Returns a single group when every package covers the same set
 * (including "everything is whole project"), so callers can skip the headers.
 */
export function groupVendorPackagesByStructure(packages: RfqPackage[]): VendorStructurePackageGroup[] {
  const sorted = sortPackages(packages);
  const lanes = new Map<string, VendorStructurePackageGroup>();
  const whole: RfqPackage[] = [];

  for (const pkg of sorted) {
    if (packageScopeFullyArchived(pkg)) continue;
    const refs = packageStructureRefs(pkg);
    if (!refs.length) {
      whole.push(pkg);
      continue;
    }
    const byId = new Map<string, string>();
    for (const r of refs) if (!byId.has(r.id)) byId.set(r.id, r.name);
    const ids = Array.from(byId.keys()).sort();
    const key = ids.join('|');
    let lane = lanes.get(key);
    if (!lane) {
      const names = ids.map(id => byId.get(id) || 'Structure');
      lane = { key, label: names.join(' + '), structureIds: ids, structureNames: names, packages: [] };
      lanes.set(key, lane);
    } else {
      // Fill in names a later package may have carried for the same ids.
      lane.structureNames = lane.structureIds.map((id, i) => lane!.structureNames[i] || byId.get(id) || 'Structure');
      lane.label = lane.structureNames.join(' + ');
    }
    lane.packages.push(pkg);
  }

  if (lanes.size === 0) {
    return [
      {
        key: VENDOR_WHOLE_PROJECT_KEY,
        label: 'Whole project',
        structureIds: [],
        structureNames: [],
        packages: sorted,
      },
    ];
  }

  const out = Array.from(lanes.values());
  if (whole.length) {
    for (const lane of out) lane.packages = sortPackages([...lane.packages, ...whole]);
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/** One Files → RFQs request round as the supplier sees it (from the packages' envelope). */
export type VendorRequestRound = {
  requestId: string;
  label: string;
  stage?: string;
  message?: string;
  quoteDeadline: Date | null;
  sentAt: Date | null;
  /** Packages in this round for this supplier, newest first. */
  packages: RfqPackage[];
  /** Structure-lane labels covered by this round ("Sale Barn", "Whole project"). */
  laneLabels: string[];
  /** Individual structure names across the round's packages (empty = whole project). */
  structureNames: string[];
  /** True while any package in this round still awaits a quote or not-bidding. */
  open: boolean;
};

function laneLabelForPackage(pkg: RfqPackage): string {
  const names = packageStructureRefs(pkg).map(r => r.name.trim()).filter(Boolean);
  return names.length ? names.join(' + ') : 'Whole project';
}

/**
 * Group a supplier's packages (one work type) by request round, newest round first.
 * Packages without `rfqRequestId` (Suppliers-tab sends) are not included — callers
 * fall back to the structure lanes alone for those.
 */
export function groupVendorPackagesByRequest(
  packages: RfqPackage[],
  submissionsByPackage?: Map<string, RfqSubmission[]>,
): VendorRequestRound[] {
  const rounds = new Map<string, VendorRequestRound>();
  for (const pkg of sortPackages(packages)) {
    if (packageScopeFullyArchived(pkg)) continue;
    const requestId = (pkg.rfqRequestId || '').trim();
    if (!requestId) continue;
    let round = rounds.get(requestId);
    if (!round) {
      round = {
        requestId,
        label: (pkg.requestLabel || '').trim() || 'Quote request',
        stage: pkg.requestStage,
        message: (pkg.requestMessage || '').trim() || undefined,
        quoteDeadline: pkg.quoteDeadline instanceof Date ? pkg.quoteDeadline : null,
        sentAt: pkg.createdAt instanceof Date ? pkg.createdAt : null,
        packages: [],
        laneLabels: [],
        structureNames: [],
        open: false,
      };
      rounds.set(requestId, round);
    }
    round.packages.push(pkg);
    const lane = laneLabelForPackage(pkg);
    if (!round.laneLabels.includes(lane)) round.laneLabels.push(lane);
    for (const ref of packageStructureRefs(pkg)) {
      const name = ref.name.trim();
      if (name && !round.structureNames.includes(name)) round.structureNames.push(name);
    }
    if (pkg.createdAt instanceof Date && (!round.sentAt || pkg.createdAt < round.sentAt)) round.sentAt = pkg.createdAt;
    if (!round.quoteDeadline && pkg.quoteDeadline instanceof Date) round.quoteDeadline = pkg.quoteDeadline;
    if (submissionsByPackage) {
      const subs = pkg.id ? submissionsByPackage.get(pkg.id) ?? [] : [];
      const answered = subs.some(s => s.notBidding || !!s.storagePath || !!s.cncQuote);
      if (!answered) round.open = true;
    }
  }
  return Array.from(rounds.values()).sort((a, b) => (b.sentAt?.getTime() ?? 0) - (a.sentAt?.getTime() ?? 0));
}

/** Newest request round, if the supplier has any request-driven packages. */
export function latestVendorRequestRound(
  packages: RfqPackage[],
  submissionsByPackage?: Map<string, RfqSubmission[]>,
): VendorRequestRound | null {
  return groupVendorPackagesByRequest(packages, submissionsByPackage)[0] ?? null;
}

/** Soonest open quote deadline across a supplier's packages (sidebar "Due" cell). */
export function nextOpenQuoteDeadline(
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
): Date | null {
  let soonest: Date | null = null;
  for (const round of groupVendorPackagesByRequest(packages, submissionsByPackage)) {
    if (!round.open || !round.quoteDeadline) continue;
    if (!soonest || round.quoteDeadline < soonest) soonest = round.quoteDeadline;
  }
  return soonest;
}

export type VendorSupersedesInfo = {
  previous: RfqPackage;
  /** Display version of the previous package in the lane's version tree, when known. */
  previousVersionIndex?: number;
  /** Latest active quote the supplier gave on the previous package. */
  previousQuote: RfqSubmission | null;
  previousQuoteTotal: number | null;
};

/** "Re-quote requested — v3 replaces v2 (quoted $X)" inputs for a package. */
export function vendorSupersedesInfo(
  pkg: RfqPackage,
  lanePackages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
): VendorSupersedesInfo | null {
  const prevId = (pkg.supersedesPackageId || '').trim();
  if (!prevId) return null;
  const previous = lanePackages.find(p => p.id === prevId);
  if (!previous) return null;
  const versions = buildVendorPackageVersions(lanePackages, submissionsByPackage);
  const prevRow = versions.find(v => v.pkg.id === prevId);
  const subs = (submissionsByPackage.get(prevId) ?? [])
    .filter(s => !s.notBidding && (!!s.storagePath || !!s.cncQuote))
    .sort((a, b) => (b.createdAt instanceof Date ? b.createdAt.getTime() : 0) - (a.createdAt instanceof Date ? a.createdAt.getTime() : 0));
  const previousQuote = subs[0] ?? null;
  const total = previousQuote ? submissionQuoteTotal(previousQuote) : null;
  return {
    previous,
    previousVersionIndex: prevRow?.versionIndex,
    previousQuote,
    previousQuoteTotal: total != null && total > 0 ? total : null,
  };
}

const PROJECT_NUMBER_PREFIX_RE = /^(#\d{3,6}|\d{2}-\d{3})(?=\s|\s*-|$)/;

export function resolveVendorPortalProjectNumberDisplay(
  dealId: string,
  dealTitle: string,
  dealsById: Map<string, Pick<EnhancedDeal, 'projectNumber' | 'title'>>,
): string | null {
  const deal = dealsById.get(dealId);
  let rawNum = (deal?.projectNumber || '').trim();
  if (!rawNum) {
    const title = (deal?.title || dealTitle || '').trim();
    const match = title.match(PROJECT_NUMBER_PREFIX_RE);
    if (match) rawNum = match[1];
  }
  if (!rawNum) return null;
  return rawNum.startsWith('#') ? rawNum : `#${rawNum}`;
}

export function resolveVendorPortalSidebarTitle(
  dealId: string,
  dealTitle: string,
  dealsById: Map<string, Pick<EnhancedDeal, 'descriptor' | 'title'>>,
  projectNumberDisplay: string | null,
): string {
  const deal = dealsById.get(dealId);
  const descriptor = (deal?.descriptor || '').trim();
  if (descriptor) return descriptor;

  const title = (deal?.title || dealTitle || '').trim();
  const fromTitle = parseLegacyTitle(title).descriptor.trim();
  if (fromTitle) return fromTitle;

  if (projectNumberDisplay && title) {
    const escaped = projectNumberDisplay.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const stripped = title
      .replace(new RegExp(`^${escaped}\\s*(-\\s*)?`, 'i'), '')
      .trim();
    const fromStripped = parseLegacyTitle(stripped).descriptor.trim();
    if (fromStripped) return fromStripped;
    if (stripped) return stripped;
  }

  return title || dealTitle;
}

function latestPackageBidTotal(
  project: RfqProject,
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
): number {
  if (!packages.length) return 0;
  const versions = buildVendorPackageVersions(packages, submissionsByPackage);
  const latest = versions.find(v => v.isLatest) ?? versions[0];
  if (!latest?.pkg.id) return 0;
  const subs = (submissionsByPackage.get(latest.pkg.id) || []).filter(sub => !sub.notBidding);
  const isCnc = normalizeWorkType(latest.pkg.workType) === 'cnc_quote';
  const approvedIds = isCnc
    ? new Set<string>()
    : approvedSubmissionIdsForPackage(project, latest.pkg, packages, submissionsByPackage);
  const keyFor = isCnc
    ? cncSubmissionHistoryKey
    : (sub: RfqSubmission) => timberQuoteStreamKey(sub);
  const groups = groupQuoteSubmissionHistory(subs, keyFor);
  return primarySubmissionsFromGroups(groups, approvedIds).reduce((sum, sub) => {
    const total = submissionQuoteTotal(sub);
    return total != null && total > 0 ? sum + total : sum;
  }, 0);
}

/** Sum quote totals on the latest timber + latest CNC package versions only. */
export function dealLatestMaterialsBidTotal(
  group: VendorPortalDealGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  options: { includeTimber: boolean; includeCnc: boolean },
): number | null {
  let total = 0;
  let included = false;
  if (options.includeTimber && group.timber) {
    total += latestPackageBidTotal(group.timber.project, group.timber.packages, submissionsByPackage);
    included = true;
  }
  if (options.includeCnc && group.cnc) {
    total += latestPackageBidTotal(group.cnc.project, group.cnc.packages, submissionsByPackage);
    included = true;
  }
  if (!included) return null;
  return total > 0 ? total : null;
}

export function dealNeedsQuoteResponse(
  group: VendorPortalDealGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
): boolean {
  if (group.timber) {
    if (
      projectNeedsQuoteResponse(
        group.timber.packages,
        submissionsByPackage,
        group.timber.project.status,
      )
    ) {
      return true;
    }
  }
  if (group.cnc) {
    if (
      projectNeedsQuoteResponse(
        group.cnc.packages,
        submissionsByPackage,
        group.cnc.project.status,
      )
    ) {
      return true;
    }
  }
  return false;
}
